import clsx from 'clsx';
import { colors } from 'components/src/foundation';
import { Paragraph } from 'components/src/typography';
import * as React from 'react';
import ArrowRight from 'components/src/svg/ArrowRight';
import ArrowLeft from 'components/src/svg/ArrowLeft';
import { AnimatePresence, motion } from 'framer-motion';

type Props = {
  value: number;
  onChange: (value: 1 | -1) => void;
};

type ButtonWrapperProps = {
  children: React.ReactNode;
  disabled: boolean;
  onClick?: () => void;
};

const variants = {
  initial: (custom: string) => ({
    x: custom === 'increase' ? 10 : -10,
    opacity: 0,
    scale: 0.8,
  }),
  animate: {
    x: 0,
    opacity: 1,
    scale: 1,
  },
  exit: (custom: string) => ({
    x: custom === 'increase' ? -10 : 10,
    opacity: 0,
    scale: 0.8,
  }),
};

const ButtonWrapper = ({ children, disabled, onClick }: ButtonWrapperProps) => {
  return (
    <motion.button
      whileTap={{ scale: 1.5 }}
      type="button"
      disabled={disabled}
      className={clsx(
        'flex h-16 w-16 items-center justify-center rounded-full transition-colors disabled:pointer-events-none disabled:cursor-not-allowed',
        {
          'opacity-10': disabled,
          'opacity-100': !disabled,
        }
      )}
      onClick={onClick}
    >
      {children}
    </motion.button>
  );
};

const QuantitySelect = ({ value, onChange }: Props) => {
  const [direction, setDirection] = React.useState<string>('');

  return (
    <div className="flex items-center gap-8 rounded-28 border-1 border-black-25 px-8 py-4">
      <ButtonWrapper
        disabled={value <= 0}
        onClick={() => {
          onChange(-1);
          setDirection('decrease');
        }}
      >
        <ArrowLeft color={colors.neutral[10]} />
      </ButtonWrapper>
      <AnimatePresence mode="popLayout" initial={false} custom={direction}>
        <motion.div
          key={value}
          custom={direction}
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.2 }}
        >
          <Paragraph classes="min-w-[11px] text-center">{value.toString()}</Paragraph>
        </motion.div>
      </AnimatePresence>
      <ButtonWrapper
        disabled={value >= 9}
        onClick={() => {
          onChange(1);
          setDirection('increase');
        }}
      >
        <ArrowRight color={colors.neutral[10]} />
      </ButtonWrapper>
    </div>
  );
};

export default QuantitySelect;
